import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TalentService } from '../talent.service';

@Component({
    selector: 'app-ai-test-list',
    templateUrl: './ai-test-list.component.html',
    styleUrls: ['./ai-test-list.component.css'],
    standalone: false
})
export class AiTestListComponent implements OnInit {
  tests: any = [];
  filteredTests: any = [];
  skills: any = [];
  
  // Filters
  selectedSkills: any = [];
  selectedDifficulty: string = '';
  minTime: number = null;
  maxTime: number = null;
  searchText: string = '';
  statusFilter: string = 'active'; // 'active', 'archived', or 'all'
  
  // Sorting
  sortField: string = 'created_at';
  sortReverse: boolean = true;
  
  isLoading: boolean = false;
  errorMessage: string = '';

  constructor(
    private talentService: TalentService,
    private router: Router
  ) {}

  ngOnInit() {
    this.loadSkills();
    this.loadTests();
  }

  loadSkills() {
    this.talentService.getTestSkills().subscribe(
      (skills) => {
        this.skills = skills;
      },
      (error) => {
        console.error('Error loading skills:', error);
      }
    );
  }

  loadTests() {
    this.isLoading = true;
    this.errorMessage = '';
    
    const filters: any = {};
    if (this.selectedSkills.length > 0) {
      filters.skill_ids = this.selectedSkills;
    }
    if (this.selectedDifficulty) {
      filters.difficulty = this.selectedDifficulty;
    }
    if (this.minTime) {
      filters.min_time = this.minTime;
    }
    if (this.maxTime) {
      filters.max_time = this.maxTime;
    }
    // Add status filter
    filters.status = this.statusFilter;

    this.talentService.getCandidateTests(Object.keys(filters).length > 0 ? filters : null).subscribe(
      (tests) => {
        this.tests = tests;
        this.applyFilters();
        this.isLoading = false;
      },
      (error) => {
        console.error('Error loading tests:', error);
        this.errorMessage = 'Failed to load tests';
        this.isLoading = false;
      }
    );
  }

  applyFilters() {
    this.filteredTests = this.tests.filter(test => {
      if (this.searchText) {
        const searchLower = this.searchText.toLowerCase();
        const matchesSearch = 
          (test.skills_string && test.skills_string.toLowerCase().includes(searchLower)) ||
          (test.id && test.id.toString().includes(searchLower)) ||
          (test.created_by_name && test.created_by_name.toLowerCase().includes(searchLower)) ||
          (test.name && test.name.toLowerCase().includes(searchLower)) ||
          (test.description && test.description.toLowerCase().includes(searchLower));
        if (!matchesSearch) return false;
      }
      return true;
    });

    // Apply sorting
    this.filteredTests.sort((a, b) => {
      let aVal = a[this.sortField];
      let bVal = b[this.sortField];
      
      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;
      
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }
      
      if (this.sortReverse) {
        return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
      } else {
        return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
      }
    });
  }

  setSortField(field: string) {
    if (this.sortField === field) {
      this.sortReverse = !this.sortReverse;
    } else {
      this.sortField = field;
      this.sortReverse = false;
    }
    this.applyFilters();
  }

  clearFilters() {
    this.selectedSkills = [];
    this.selectedDifficulty = '';
    this.minTime = null;
    this.maxTime = null;
    this.searchText = '';
    this.statusFilter = 'active';
    this.loadTests();
  }

  viewTest(testId: number) {
    this.router.navigate(['/ai-test-detail', testId]);
  }

  createNewTest() {
    this.router.navigate(['/ai-test-generation']);
  }

  navigateToAssignments() {
    this.router.navigate(['/test-assignments']);
  }

  editTest(testId: number, event: Event) {
    event.stopPropagation();
    this.router.navigate(['/ai-test-generation'], { 
      queryParams: { edit: true, testId: testId } 
    });
  }

  cloneTest(testId: number, event: Event) {
    event.stopPropagation();
    
    if (!confirm('Create a copy of this test?')) {
      return;
    }

    this.talentService.cloneCandidateTest(testId).subscribe(
      (response) => {
        if (response.status === 'success') {
          alert('Test cloned successfully!');
          this.loadTests();
        } else {
          alert(response.message || 'Failed to clone test');
        }
      },
      (error) => {
        console.error('Error cloning test:', error);
        alert('Failed to clone test');
      }
    );
  }

  archiveTest(test: any, event: Event) {
    event.stopPropagation();
    
    const isArchived = test.status === 'archived';
    const action = isArchived ? 'unarchive' : 'archive';

    this.talentService.archiveCandidateTest(test.id).subscribe(
      (response) => {
        if (response.status === 'success') {
          this.loadTests();
        } else {
          alert(response.message || `Failed to ${action} test`);
        }
      },
      (error) => {
        console.error(`Error ${action}ing test:`, error);
        alert(`Failed to ${action} test`);
      }
    );
  }

  deleteTest(testId: number, event: Event) {
    event.stopPropagation();
    
    if (!confirm('Are you sure you want to delete this test? This action cannot be undone.')) {
      return;
    }

    this.talentService.deleteCandidateTest(testId).subscribe(
      (response) => {
        if (response.status === 'success') {
          this.loadTests();
        } else {
          alert(response.message || 'Failed to delete test');
        }
      },
      (error) => {
        console.error('Error deleting test:', error);
        alert('Failed to delete test');
      }
    );
  }

  getDifficultyDisplay(difficulty: string): string {
    if (!difficulty) return 'N/A';
    return difficulty.charAt(0).toUpperCase() + difficulty.slice(1);
  }

  getDifficultyBadgeClass(difficulty: string): string {
    switch(difficulty) {
      case 'basic': return 'badge-success';
      case 'intermediate': return 'badge-warning';
      case 'advanced': return 'badge-danger';
      default: return 'badge-secondary';
    }
  }

  formatDate(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  }
}
